import {
  PRICE_TECHNICAL_ERROR_HEADING,
  PRICE_TECHNICAL_ERROR_BODY,
  PRICE_GENERAL_ERROR_HEADING,
  PRICE_GENERAL_ERROR_BODY,
  PRICE_WIDTH_EXCEEDS_MAXIMUM_ERROR_HEADING,
  PRICE_DROP_EXCEEDS_MAXIMUM_ERROR_HEADING,
  PRICE_EXCEEDS_MAXIMUM_ERROR_BODY,
  PRICE_REQUIRES_JOINS_ERROR_HEADING,
  PRICE_REQUIRES_JOINS_ERROR_BODY,
  PRICE_TOO_HEAVY_FABRIC_ERROR_HEADING,
  PRICE_TOO_HEAVY_FABRIC_ERROR_BODY,
} from "./constants";
import {
  RomanBlindConfiguration,
  CurtainConfiguration,
  MadeToMeasureConfiguration,
  LiningWeight,
} from "../configurationTypes";

type M2MErrorDetails = {
  maxWidth?: string;
  maxRailSize?: string;
  maxDrop?: string;
};

export type M2MError = {
  heading: string;
  body: string;
  details?: M2MErrorDetails;
  type: string;
};

export enum M2MErrorType {
  GENERAL = "general",
  WIDTH = "width",
  HEIGHT = "height",
}

type M2MPriceData = { total: string; blackoutLiningPrice?: string };

type M2MPriceResponse = {
  price?: M2MPriceData;
  error?: M2MError;
};

export async function getM2MPrice(
  baseUrl: string,
  productType: string,
  fabric: object,
  quantity: number,
  configuration: MadeToMeasureConfiguration,
): Promise<M2MPriceResponse> {
  const { url, options } = requestBuilder(
    baseUrl,
    productType,
    fabric,
    quantity,
    configuration,
  );

  let response;

  try {
    response = await fetch(url, options);
  } catch (err) {
    return {
      error: createErrorMessage(
        PRICE_TECHNICAL_ERROR_HEADING,
        PRICE_TECHNICAL_ERROR_BODY,
      ),
    };
  }

  const json = await response.json();

  if (!response.ok) {
    return { error: processErrorResponse(json.description, json.details) };
  }

  if (json.price) {
    return {
      price: { total: `£${json.price.amount.toFixed(2)}` },
    } as M2MPriceResponse;
  } else {
    return {
      error: createErrorMessage(
        PRICE_GENERAL_ERROR_HEADING,
        PRICE_GENERAL_ERROR_BODY,
      ),
    };
  }
}

export async function getM2MPriceWithBlackout(
  baseUrl: string,
  productType: string,
  fabric: object,
  quantity: number,
  configuration: CurtainConfiguration | RomanBlindConfiguration,
): Promise<M2MPriceResponse> {
  const { url, options } = requestBuilder(
    baseUrl,
    productType,
    fabric,
    quantity,
    configuration,
  );

  const inverseOptions: RequestInit = {
    ...options,
    body: JSON.stringify({
      fabric,
      quantity,
      configuration: {
        ...configuration,
        liningWeight: getInverseLiningWeight(configuration.liningWeight),
      },
    }),
  };

  let selectedResponse;
  let inverseResponse;
  let allResponses;

  try {
    selectedResponse = fetch(url, options);
    inverseResponse = fetch(url, inverseOptions);

    allResponses = await Promise.all([selectedResponse, inverseResponse])
      .then((values) => {
        return values;
      })
      .catch((error) => {
        throw new Error(error);
      });
  } catch (err) {
    return {
      error: createErrorMessage(
        PRICE_TECHNICAL_ERROR_HEADING,
        PRICE_TECHNICAL_ERROR_BODY,
      ),
    };
  }
  const selectedResponseBody = await allResponses[0].json();
  const inverseResponseBody = await allResponses[1].json();

  if (!allResponses[0].ok) {
    return {
      error: processErrorResponse(
        selectedResponseBody.description,
        selectedResponseBody.details,
      ),
    };
  }

  if (!allResponses[1].ok) {
    return {
      error: processErrorResponse(
        inverseResponseBody.description,
        inverseResponseBody.details,
      ),
    };
  }

  if (selectedResponseBody.price && inverseResponseBody.price) {
    const blackoutLiningPrice = calculateBlackoutPriceDifference(
      selectedResponseBody.price?.amount,
      inverseResponseBody.price?.amount,
    );
    return {
      price: {
        total: `£${selectedResponseBody.price.amount.toFixed(2)}`,
        blackoutLiningPrice,
      },
    } as M2MPriceResponse;
  } else {
    return {
      error: createErrorMessage(
        PRICE_GENERAL_ERROR_HEADING,
        PRICE_GENERAL_ERROR_BODY,
      ),
    };
  }
}

function getInverseLiningWeight(liningWeight: LiningWeight): LiningWeight {
  return liningWeight === LiningWeight.Blackout
    ? LiningWeight.Standard
    : LiningWeight.Blackout;
}

function calculateBlackoutPriceDifference(
  price1: number,
  price2: number,
): string {
  if (!price1 || !price2) {
    return "";
  }
  return `£${Math.abs(price1 - price2)
    .toFixed(2)
    .toString()}`;
}

function processErrorResponse(
  errorDescription: string,
  details?: M2MErrorDetails,
): M2MError {
  if (errorDescription) {
    if (
      errorDescription.toLowerCase() === "invalid width" ||
      errorDescription.toLowerCase() === "invalid railsize"
    ) {
      return createErrorMessage(
        PRICE_WIDTH_EXCEEDS_MAXIMUM_ERROR_HEADING.replace(
          "{0}",
          details?.maxWidth || details?.maxRailSize || "",
        ),
        PRICE_EXCEEDS_MAXIMUM_ERROR_BODY,
        details,
        M2MErrorType.WIDTH,
      );
    }
    if (errorDescription.toLowerCase() === "invalid drop") {
      return createErrorMessage(
        PRICE_DROP_EXCEEDS_MAXIMUM_ERROR_HEADING.replace(
          "{0}",
          details?.maxDrop || "",
        ),
        PRICE_EXCEEDS_MAXIMUM_ERROR_BODY,
        details,
        M2MErrorType.HEIGHT,
      );
    }
    if (errorDescription.toLowerCase() === "product requires joins") {
      return createErrorMessage(
        PRICE_REQUIRES_JOINS_ERROR_HEADING,
        PRICE_REQUIRES_JOINS_ERROR_BODY,
      );
    }
    if (errorDescription.toLowerCase().startsWith("the weight of the fabric")) {
      return createErrorMessage(
        PRICE_TOO_HEAVY_FABRIC_ERROR_HEADING,
        PRICE_TOO_HEAVY_FABRIC_ERROR_BODY,
      );
    }
    if (errorDescription.toLowerCase() === "fabric unavailable") {
      return createErrorMessage(
        PRICE_GENERAL_ERROR_HEADING,
        PRICE_GENERAL_ERROR_BODY,
      );
    }
  }
  return createErrorMessage(
    PRICE_TECHNICAL_ERROR_HEADING,
    PRICE_TECHNICAL_ERROR_BODY,
  );
}

function requestBuilder(
  baseUrl: string,
  productType: string,
  fabric: object,
  quantity: number,
  configuration: MadeToMeasureConfiguration,
) {
  const body = { fabric, quantity, configuration };
  const url = `${baseUrl}/${productType}/configurations`;

  const options: RequestInit = {
    method: "POST",
    credentials: "omit",
    body: JSON.stringify(body),
    headers: {
      "Content-Type": "application/json",
    },
  };

  return { url, options };
}

function createErrorMessage(
  heading: string,
  body: string,
  details?: M2MErrorDetails,
  errorType?: M2MErrorType,
): M2MError {
  const type = errorType || M2MErrorType.GENERAL;
  const response: M2MError = { heading, body, type };

  if (details) {
    response.details = details;
  }

  return response;
}
